import { memo } from 'react';
import type { CSSProperties, MouseEvent, PointerEvent } from 'react';
import type { ThreadView } from '../lib/api';
import { ROW_HEIGHTS, type Density, type RowLines } from '../lib/store';
import { absoluteTimestamp, formatParticipants, formatTimestamp } from '../lib/format';
import styles from './ThreadRow.module.css';

/**
 * The measured Superhuman row: 36px of pitch, ~14px sender, no avatar anywhere,
 * subject and preview on the SAME single line, and a small coloured dot at the
 * left edge whose slot is reserved whether or not the dot is drawn.
 *
 * There is deliberately no avatar and no `showAvatar` prop. `appearance
 * .showAvatars` still exists in the store because AppearanceSettings owns that
 * toggle, but nothing in the list reads it any more: the row has no place to put
 * a 22px circle without breaking the 36px pitch, and the account dot already
 * answers the only question an avatar was answering here ("whose is this").
 *
 * The dot is THE ONE PLACE Kiln must diverge. Superhuman is single-account, so
 * their dot only has to mean "unread". Kiln unifies eight mailboxes, so ours
 * carries two dimensions in one 8px element: the hue is the ACCOUNT, and the
 * opacity is the unread state — full when unread, ~22% when read.
 */

export type { Density };

export function rowHeightFor(density: Density, lines: RowLines): number {
  return ROW_HEIGHTS[lines][density];
}

export interface ThreadRowProps {
  thread: ThreadView;
  density: Density;
  /** One line is the spec; two is the narrow-window fallback the list decides. */
  lines: RowLines;
  showSnippet: boolean;
  /** Hex colours for `thread.accountIds`, already resolved by the list. */
  accountColors: string[];
  selfAddresses: string[];
  focused: boolean;
  /** Part of a multi-select (`x`). */
  checked: boolean;
  /** Currently open in the reading view. */
  active: boolean;
  /** Shared clock so a list of rows agrees on what "now" means. */
  now: number;
  onOpen: (threadKey: string, event: MouseEvent<HTMLElement>) => void;
  onPointerEnter: (threadKey: string) => void;
  onToggleStar: (threadKey: string, next: boolean) => void;
}

/** CSS custom properties are not part of `CSSProperties`; this is the escape. */
function cssVars(vars: Record<string, string | number>): CSSProperties {
  return vars as unknown as CSSProperties;
}

/**
 * One element, so the row keeps exactly one 8px slot no matter how many
 * mailboxes a thread landed in. A thread that reached two accounts splits the
 * dot rather than growing a second one.
 */
function dotFill(colors: string[]): string {
  const first = colors[0] ?? 'var(--accent-9)';
  const second = colors[1];
  if (second === undefined) return first;
  return `linear-gradient(90deg, ${first} 0 50%, ${second} 50% 100%)`;
}

function PaperclipIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M9.9 4.1 5.2 8.8a1.4 1.4 0 0 0 2 2l4.6-4.7a2.8 2.8 0 0 0-4-4L3.2 6.9a4.2 4.2 0 0 0 6 6L13 9"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function StarIcon({ filled }: { filled: boolean }) {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M8 1.8l1.86 3.77 4.16.6-3.01 2.93.71 4.14L8 11.29l-3.72 1.95.71-4.14L1.98 6.17l4.16-.6z"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PinIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M4.4 1.5h3.2l-.4 3 2 1.8v.9H2.8v-.9l2-1.8zM6 7.2V10.5"
        stroke="currentColor"
        strokeWidth="1.1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ThreadRowImpl({
  thread,
  density,
  lines,
  showSnippet,
  accountColors,
  selfAddresses,
  focused,
  checked,
  active,
  now,
  onOpen,
  onPointerEnter,
  onToggleStar,
}: ThreadRowProps) {
  const unread = thread.unreadCount > 0;
  const senders = formatParticipants(thread.participants, selfAddresses);
  const time = formatTimestamp(thread.lastDate, now);
  const timeTitle = absoluteTimestamp(thread.lastDate);

  const sender = (
    <span className={styles.senderCell}>
      <span className={styles.sender}>{senders}</span>
      {/* "Lily Hennes (3)" — the count is part of the name, not a badge. */}
      {thread.messageCount > 1 && <span className={styles.count}>({thread.messageCount})</span>}
    </span>
  );

  const subjectRun = (
    <span className={styles.subjectLine}>
      <span className={styles.subject}>{thread.subject || '(no subject)'}</span>
      {showSnippet && thread.snippet && <span className={styles.snippet}>{thread.snippet}</span>}
    </span>
  );

  /* The whole meta group hides on hover, because the triage cluster the list
     floats over the row takes this exact space. */
  const meta = (
    <span className={styles.meta}>
      {thread.pinned && (
        <span className={styles.glyph} title="Pinned">
          <PinIcon />
        </span>
      )}
      {/* Only attachment and pin are drawn: the thread payload carries no invite
          or comment signal, and a glyph that guesses is worse than no glyph. */}
      {thread.hasAttachments && (
        <span className={styles.glyph} title="Has attachments">
          <PaperclipIcon />
        </span>
      )}
      <button
        type="button"
        className={styles.star}
        data-on={thread.flagged}
        title={thread.flagged ? 'Unstar · s' : 'Star · s'}
        aria-label={thread.flagged ? 'Unstar thread' : 'Star thread'}
        aria-pressed={thread.flagged}
        tabIndex={-1}
        onClick={(e) => {
          e.stopPropagation();
          onToggleStar(thread.threadKey, !thread.flagged);
        }}
      >
        <StarIcon filled={thread.flagged} />
      </button>
      <span className={styles.time} title={timeTitle}>
        {time}
      </span>
    </span>
  );

  return (
    <div
      className={styles.row}
      role="option"
      aria-selected={active}
      data-thread-key={thread.threadKey}
      data-density={density}
      data-lines={lines}
      data-unread={unread}
      data-focused={focused}
      data-checked={checked}
      data-active={active}
      onClick={(e) => onOpen(thread.threadKey, e)}
      onPointerEnter={(e: PointerEvent<HTMLDivElement>) => {
        if (e.pointerType === 'mouse') onPointerEnter(thread.threadKey);
      }}
    >
      {/* Always rendered, never conditional: the slot is what stops a row from
          reflowing the instant it is marked read. */}
      <span
        className={styles.dot}
        style={cssVars({ '--dot-fill': dotFill(accountColors) })}
        aria-hidden="true"
      />

      {lines === 1 ? (
        <>
          {sender}
          {subjectRun}
          {meta}
        </>
      ) : (
        <div className={styles.stacked}>
          <div className={styles.stackedLine}>
            {sender}
            <span className={styles.spacer} />
            {meta}
          </div>
          <div className={styles.stackedLine}>{subjectRun}</div>
        </div>
      )}
    </div>
  );
}

/**
 * `accountColors` is derived per row, so its identity changes every render and a
 * default shallow compare would defeat memoisation on the one prop that must
 * not — scrolling re-renders every visible row otherwise.
 */
function propsEqual(a: ThreadRowProps, b: ThreadRowProps): boolean {
  if (a.accountColors.length !== b.accountColors.length) return false;
  for (let i = 0; i < a.accountColors.length; i += 1) {
    if (a.accountColors[i] !== b.accountColors[i]) return false;
  }
  return (
    a.thread === b.thread &&
    a.density === b.density &&
    a.lines === b.lines &&
    a.showSnippet === b.showSnippet &&
    a.selfAddresses === b.selfAddresses &&
    a.focused === b.focused &&
    a.checked === b.checked &&
    a.active === b.active &&
    a.now === b.now &&
    a.onOpen === b.onOpen &&
    a.onPointerEnter === b.onPointerEnter &&
    a.onToggleStar === b.onToggleStar
  );
}

export const ThreadRow = memo(ThreadRowImpl, propsEqual);
